import { lazy } from "react"
import { Navigate, Route, Routes } from "react-router-dom"
import ChargerDetailsProvider from "../components/ChargerDetailsModal/ChargerDetailsProvider"

// All imports lazy-loaded
import PermissionRoute from "permissions/PermissionRoute"

// Lazy imports
const Layout = lazy(() => import("layouts/Layout"))
const DemolitionList = lazy(() => import("pages/Operations/features/Demolition/DemolitionList"))
const InstallationList = lazy(() => import("pages/Operations/features/Installation/InstallationList"))
const SurveyList = lazy(() => import("pages/Operations/features/Survey/features/SurveyList"))
const ProjectsList = lazy(() => import("pages/Projects/features/ProjectsList"))
const Sites = lazy(() => import("pages/Sites"))
const SiteTicketDetails = lazy(() => import("pages/Sites/features/SiteTicketDetails"))
const WarehouseProfile = lazy(() => import("pages/Sites/features/StatiqWarehouses/WarehouseProfile"))
const NotFound = lazy(() => import("../pages/Error/404"))
const DemolitionTicketDetails = lazy(() => import("pages/Operations/features/Demolition/DemolitionTicketDetails"))
const Approvals = lazy(() => import("pages/Approvals"))
const ProjectTicketDetails = lazy(() => import("../pages/Projects/features/ProjectTicketDetails"))
const SurveyTicketDetails = lazy(() => import("../pages/Operations/features/Survey/features/SurveyTicketDetails"))
const FinanceticketDetails = lazy(() => import("../pages/Finance/features/FinanceTicketDetail/index"))
const InstallationTicketDetails = lazy(
    () => import("../pages/Operations/features/Installation/InstallationTicketDetails")
)
const EvApplicationTicketDetails = lazy(
    () => import("../pages/Operations/features/EVApplication/EvApplicationTicketDetails/index")
)

const InstallationFeedback = lazy(() => import("../pages/InstallationFeedback"))
const KiaWarrantyRegistration = lazy(() => import("../pages/KiaWarrantyRegistration"))

const LandingLayout = lazy(() => import("../layouts/LandingLayout"))
const SideBarLayout = lazy(() => import("../layouts/SideBarLayout"))
const RequiredAuth = lazy(() => import("./RequiredAuth"))
const Login = lazy(() => import("../pages/Auth").then(({ Login }) => ({ default: Login })))
const OTPverification = lazy(() =>
    import("../pages/Auth").then(({ OTPverification }) => ({ default: OTPverification }))
)
const Admin = lazy(() => import("../pages/UserManagement/index"))
const Complaints = lazy(() => import("../pages/Complaints"))
const Accounts = lazy(() => import("../pages/Accounts"))
const Setting = lazy(() => import("../pages/Settings/index"))
const EvApplication = lazy(() => import("../pages/Operations/EvApplication"))
const DealDetail = lazy(() => import("../pages/Operations/features/DealDetails/index"))
const Logistics = lazy(() => import("../pages/Logistics/index"))
const LogisticsDealDetail = lazy(() => import("../pages/Logistics/features/LogisticsDealDetail/index"))
const DispatchRequest = lazy(() => import("../pages/DispatchRequest/index"))
const DispatchRequestDetail = lazy(() => import("../pages/DispatchRequest/features/DispatchRequestDetail/index"))
const WarehouseOutward = lazy(() => import("../pages/WarehouseOutward/index"))
const WarehouseOutwardDetail = lazy(() => import("../pages/WarehouseOutward/features/WarehouseOutwardDetail/index"))
const OnboardingsDealDetail = lazy(() => import("../pages/Onboardings/features/OnboardingDealDetail/index"))
const Onboardings = lazy(() => import("../pages/Onboardings/index"))
const Finance = lazy(() => import("../pages/Finance/index"))
const Demolition = lazy(() => import("../pages/Operations/Demolition"))
const SurveyDealDetails = lazy(() => import("../pages/Operations/features/Survey/features/Survey"))
const FinanceDealDetail = lazy(() => import("../pages/Finance/features/FinanceTicketDetail/index"))
const TechSupportDealDetail = lazy(() => import("../pages/TechSupport/features/TechSupportDealDetail/index"))
const CustomerSupport = lazy(() => import("../pages/CustomerSupport"))
const TechSupport = lazy(() => import("../pages/TechSupport/index"))
const NetworkReliability = lazy(() => import("../pages/NetworkReliability/index"))
const NetworkReliabilityDealDetail = lazy(
    () => import("../pages/NetworkReliability/features/NetworkReliabilityDealDetail/index")
)
const CustomerSupportDealDetail = lazy(
    () => import("../pages/CustomerSupport/features/CustomerSupportDealDetail/index")
)
const Programs = lazy(() => import("../pages/Programs"))
const PurchaseOrders = lazy(() => import("../pages/PurchaseOrders"))
const PurchaseOrderDetail = lazy(() => import("../pages/PurchaseOrders/features/PurchaseOrderDetail"))
const CreateSalesOrderFromCRM = lazy(() => import("../pages/PurchaseOrders/features/CreateSalesOrderFromCRM"))
const ProgramDealDetails = lazy(() => import("../pages/Programs/features/ProgramDealDetails/index"))
const DeliveryVendors = lazy(() => import("../pages/ThirdParty/features/DeliveryVendors"))
const TableHistory = lazy(() => import("../pages/Accounts/features/AccountsTable/HistoryPage"))
const SitesHistory = lazy(() => import("../pages/Sites/features/SitesTable/HistoryPage"))
const NetworkReliabilityVendors = lazy(() => import("../pages/ThirdParty/features/NetworkReliabilityVendors"))
const OperationVendors = lazy(() => import("../pages/ThirdParty/features/OpsVendors"))
const DemolitionDealDetail = lazy(() => import("../pages/Operations/features/Demolition/features/DemolitionDealDetail"))
const Home = lazy(() => import("../pages/Home"))
const CreateSurveyFromCRM = lazy(() => import("../pages/Operations/features/CreateSurveyFromCRM"))
const HandoverTakeoverList = lazy(() => import("../pages/HandoverTakeover/features/HotoList"))
const HandoverTakeoverDetail = lazy(() => import("../pages/HandoverTakeover/features/HotoDetail"))

export const APP_ROUTES = {
    APPROVALS_LIST: "/approvals",
    PURCHASE_ORDERS_LIST: "/PurchaseOrders",
    PURCHASE_ORDERS_DETAILS: "/PurchaseOrders/:id",
    // Opened from the Create Sales Order button on a Zoho CRM Outright Sale deal.
    SALES_ORDER_CREATE_FROM_CRM: "/PurchaseOrders/new",
    PROJECT_LIST: "/projects",
    PROJECT_TICKET_DETAILS: "/projects/:id",
    SITES_LIST: "/sites",
    SITES_TICKET_DETAILS: "/sites/:id",
    WAREHOUSE_PROFILE: "/sites/warehouses/:id",
    ACCOUNTS_LIST: "/accounts",
    SURVEY_LIST: "/survey",
    SURVEY_TICKET_DETAILS: "/survey/:id",
    // Opened from the Create survey button on a Zoho CRM deal.
    SURVEY_CREATE_FROM_CRM: "/survey/new",
    ONBOARDING_LIST: "/onboarding",
    // A link target, not a route path: the details page is the query-string route
    // `/OnboardingDealDetails` below, so the ticket id rides in the query.
    ONBOARDING_TICKET_DETAILS: "/OnboardingDealDetails?sub_module=Onboarding&ticket_id=:id",
    INSTALLATION_LIST: "/installation",
    INSTALLATION_TICKET_DETAILS: "/installation/:id",
    FINANCE_LIST: "/finance",
    FINANCE_TICKET_DETAILS: "/finance/:id",
    DEMOLITIONS_LIST: "/demolition",
    DEMOLITIONS_TICKET_DETAILS: "/demolition/:id",
    EV_APPLICATION_LIST: "/eva",
    EV_APPLICATION_TICKET_DETAILS: "/eva/:id",
    DISPATCH_REQUEST_LIST: "/DispatchRequest",
    DISPATCH_REQUEST_TICKET_DETAILS: "/DispatchRequest/:id",
    WAREHOUSE_OUTWARD_LIST: "/WarehouseOutward",
    WAREHOUSE_OUTWARD_TICKET_DETAILS: "/WarehouseOutward/:id",
    HANDOVER_TAKEOVER_LIST: "/handover-takeover",
    HANDOVER_TAKEOVER_DETAILS: "/handover-takeover/:id",
    INSTALLATION_FEEDBACK: "/installation-feedback/:project_id",
    KIA_WARRANTY_REGISTRATION: "/warranty-registration",
}

export const getRoutesWithId = (route: string, id: string | number) => {
    return route.replace(":id", String(id))
}

const AppRoutes = () => {
    return (
        <Routes>
            {/* Public Routes — no auth required (shared via WhatsApp) */}
            <Route path={APP_ROUTES.INSTALLATION_FEEDBACK} element={<InstallationFeedback />} />
            <Route path={APP_ROUTES.KIA_WARRANTY_REGISTRATION} element={<KiaWarrantyRegistration />} />

            {/* Public Routes */}
            <Route element={<LandingLayout />}>
                <Route path="/auth" element={<Login />} />
                <Route path="/OTPverification" element={<OTPverification />} />
            </Route>

            {/* Private Routes */}
            <Route element={<RequiredAuth />}>
                <Route element={<SideBarLayout />}>
                    <Route path={APP_ROUTES.ACCOUNTS_LIST} element={<Accounts />} />
                    <Route path={"/home"} element={<Home />} />
                    <Route path={"/"} element={<Navigate to="/home" replace />} />
                    <Route
                        path="/DealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <DealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route path="/SurveyDealDetails" element={<SurveyDealDetails />} />
                    <Route path={APP_ROUTES.EV_APPLICATION_LIST} element={<EvApplication />} />
                    <Route
                        path="/Logistics"
                        element={
                            <ChargerDetailsProvider>
                                <Logistics />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/LogisticsDealDetails"
                        element={
                            <PermissionRoute permission="LOGISTICS__VIEW_TICKETS">
                                <ChargerDetailsProvider>
                                    <LogisticsDealDetail />
                                </ChargerDetailsProvider>
                            </PermissionRoute>
                        }
                    />
                    {/* Warehouse Outward — SCM module, picklist/pack/handover. Backed by
                        /v3/warehouse_outward/*, see WarehouseOutwardServices.ts */}
                    <Route
                        path={APP_ROUTES.WAREHOUSE_OUTWARD_LIST}
                        element={
                            <ChargerDetailsProvider>
                                <WarehouseOutward />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path={APP_ROUTES.WAREHOUSE_OUTWARD_TICKET_DETAILS}
                        element={
                            <PermissionRoute permission="WAREHOUSE_OUTWARD__VIEW_TICKETS">
                                <ChargerDetailsProvider>
                                    <WarehouseOutwardDetail />
                                </ChargerDetailsProvider>
                            </PermissionRoute>
                        }
                    />
                    {/* <Route path="/Finance" element={<Finance />} /> */}
                    {/* <Route path="/Survey" element={<Survey />} /> */}
                    {/* <Route path="/Demolition" element={<Demolition />} /> */}
                    <Route path="/CustomerSupport" element={<CustomerSupport />} />
                    <Route path="/TechSupport" element={<TechSupport />} />
                    <Route path="/NetworkReliability" element={<NetworkReliability />} />
                    <Route path="/Onboarding" element={<Onboardings />} />
                    <Route path="/AccountHistory" element={<TableHistory />} />
                    <Route path="/SiteHistory" element={<SitesHistory />} />
                    <Route
                        path="/OnboardingDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <OnboardingsDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/FinanceDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <FinanceDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/DemolitionDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <DemolitionDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/TechSupportDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <TechSupportDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/NetworkReliabilityDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <NetworkReliabilityDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route
                        path="/CustomerSupportDealDetails"
                        element={
                            <ChargerDetailsProvider>
                                <CustomerSupportDealDetail />
                            </ChargerDetailsProvider>
                        }
                    />
                    <Route path="/Admin" element={<Admin />} />
                    <Route path="/Settings" element={<Setting />} />
                    <Route path={"/DeliveryVendors"} element={<DeliveryVendors />} />
                    <Route path={"/NetworkReliabilityVendors"} element={<NetworkReliabilityVendors />} />
                    <Route path={"/OperationVendors"} element={<OperationVendors />} />
                    {/* <Route path={"/Projects"} element={<Projects />} /> */}
                    <Route path={"/Complaints"} element={<Complaints />} />
                    <Route path={"/Programs"} element={<Programs />} />
                    <Route path={APP_ROUTES.PURCHASE_ORDERS_LIST} element={<PurchaseOrders />} />
                    <Route path={"/ProgramDealDetails"} element={<ProgramDealDetails />} />
                    <Route path="*" element={<NotFound />} />

                    {/* Routes with the new Layout */}
                    <Route element={<Layout />}>
                        <Route path={APP_ROUTES.PROJECT_LIST} element={<ProjectsList />} />
                        <Route
                            path={APP_ROUTES.PROJECT_TICKET_DETAILS}
                            element={
                                <PermissionRoute
                                    permission="PROJECTS__VIEW_TICKETS"
                                    queryPermissions={[
                                        { param: "t", map: { "ticket-details": "PROJECTS__VIEW_LINKED_TICKETS" } },
                                        {
                                            param: "st",
                                            map: {
                                                "sales-details": "PROJECTS__VIEW_SALES_TAB",
                                                "infra-details": "PROJECTS__VIEW_INFRA_TAB",
                                                "onboarding-details": "PROJECTS__VIEW_ONBOARDING_TAB",
                                                "project-budget": "PROJECTS__VIEW_PROJECT_BUDGET",
                                                "doc-hub": "PROJECTS__VIEW_DOC_HUB",
                                            },
                                        },
                                    ]}>
                                    <ProjectTicketDetails />
                                </PermissionRoute>
                            }
                        />
                        <Route path={APP_ROUTES.SITES_LIST} element={<Sites />} />
                        <Route
                            path={APP_ROUTES.SITES_TICKET_DETAILS}
                            element={
                                <PermissionRoute permission="SITES__VIEW_TICKET_DETAIL">
                                    <SiteTicketDetails />
                                </PermissionRoute>
                            }
                        />
                        <Route
                            path={APP_ROUTES.WAREHOUSE_PROFILE}
                            // Gated inside on "View Warehouse" (useSiteTabAccess), like its Sites tab.
                            element={<WarehouseProfile />}
                        />
                        <Route path={APP_ROUTES.SURVEY_LIST} element={<SurveyList />} />
                        {/* Opened from the Create survey button on a Zoho CRM
                            deal. Belongs inside Layout like every other page:
                            Layout supplies the header and the scroll container
                            this page is taller than. */}
                        <Route path={APP_ROUTES.SURVEY_CREATE_FROM_CRM} element={<CreateSurveyFromCRM />} />
                        {/* Opened from the Create Sales Order button on a Zoho CRM deal, and
                            laid out as the survey page above is: inside Layout, which supplies the
                            header, the page title and the scroll container. A static segment
                            outranks /PurchaseOrders/:id in react-router v6, so "new" is never
                            read as a PO id. */}
                        <Route path={APP_ROUTES.SALES_ORDER_CREATE_FROM_CRM} element={<CreateSalesOrderFromCRM />} />
                        {/* Inside Layout like Sites and Projects details, which supply the page
                            title and the back arrow to /PurchaseOrders. */}
                        <Route path={APP_ROUTES.PURCHASE_ORDERS_DETAILS} element={<PurchaseOrderDetail />} />
                        <Route
                            path={APP_ROUTES.SURVEY_TICKET_DETAILS}
                            element={
                                <PermissionRoute permission="SURVEY__VIEW_TICKETS">
                                    <SurveyTicketDetails />
                                </PermissionRoute>
                            }
                        />
                        <Route path={APP_ROUTES.INSTALLATION_LIST} element={<InstallationList />} />
                        <Route
                            path={APP_ROUTES.INSTALLATION_TICKET_DETAILS}
                            element={
                                <PermissionRoute permission="INSTALLATION__VIEW_TICKETS">
                                    <InstallationTicketDetails />
                                </PermissionRoute>
                            }
                        />
                        <Route path={APP_ROUTES.DEMOLITIONS_LIST} element={<DemolitionList />} />
                        <Route
                            path={APP_ROUTES.DEMOLITIONS_TICKET_DETAILS}
                            element={
                                <PermissionRoute permission="DEMOLITION__VIEW_TICKETS">
                                    <DemolitionTicketDetails />
                                </PermissionRoute>
                            }
                        />
                        <Route path={APP_ROUTES.FINANCE_LIST} element={<Finance />} />
                        <Route
                            path={APP_ROUTES.FINANCE_TICKET_DETAILS}
                            element={
                                <PermissionRoute
                                    permission="FINANCE__VIEW_TICKETS"
                                    queryPermissions={[
                                        {
                                            param: "t",
                                            map: {
                                                "ticket-details": "FINANCE__VIEW_LINKED_TICKETS",
                                                history: "FINANCE__VIEW_TICKET_HISTORY",
                                            },
                                        },
                                        {
                                            param: "st",
                                            map: {
                                                overview: "FINANCE__VIEW_OVERVIEW",
                                                "sales-details": "FINANCE__VIEW_SALES",
                                                "material-details": "FINANCE__VIEW_MATERIALS",
                                                "doc-hub": "FINANCE__VIEW_DOC_HUB",
                                            },
                                        },
                                    ]}>
                                    <FinanceticketDetails />
                                </PermissionRoute>
                            }
                        />
                        {/* Reached by URL until the backend seeds HOTO's submodule: the sidebar
                            renders only the modules the login's role returns. Access is checked
                            in the page, since HOTO has no PERMISSIONS entries to route on yet. */}
                        <Route path={APP_ROUTES.HANDOVER_TAKEOVER_LIST} element={<HandoverTakeoverList />} />
                        <Route path={APP_ROUTES.HANDOVER_TAKEOVER_DETAILS} element={<HandoverTakeoverDetail />} />
                        <Route path={APP_ROUTES.APPROVALS_LIST} element={<Approvals />} />
                        <Route
                            path={APP_ROUTES.EV_APPLICATION_TICKET_DETAILS}
                            element={<EvApplicationTicketDetails />}
                        />
                        <Route path={APP_ROUTES.DISPATCH_REQUEST_LIST} element={<DispatchRequest />} />
                        <Route
                            path={APP_ROUTES.DISPATCH_REQUEST_TICKET_DETAILS}
                            element={
                                <PermissionRoute permission="DISPATCH_REQUEST__VIEW_TICKETS">
                                    <DispatchRequestDetail />
                                </PermissionRoute>
                            }
                        />
                    </Route>
                </Route>
            </Route>
        </Routes>
    )
}

export default AppRoutes
